import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged, UserCredential } from 'firebase/auth';
import { useDispatch } from 'react-redux';
import { setUser, clearUser, setLoading, setError as setReduxError } from '../store/slices/authSlice';
import { db } from '../config/firebase';
import { doc, setDoc } from 'firebase/firestore';

// Helper function to get user-friendly error messages
const getErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Invalid email address format.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'Account not found. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    case 'auth/configuration-not-found':
      return 'Authentication service is not configured correctly. Please contact support.';
    case 'auth/email-already-in-use':
      return 'This email is already in use. Please try a different email or sign in.';
    default:
      return 'An error occurred. Please try again.';
  }
};

interface AuthContextType {
  currentUser: any;
  signUp: (email: string, password: string) => Promise<UserCredential | void>;
  signIn: (email: string, password: string) => Promise<UserCredential | void>;
  signOut: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setAuthLoading] = useState(true);
  const [error, setAuthError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const signUp = async (email: string, password: string) => {
    try {
      dispatch(setLoading(true));
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create a new user document in Firestore
      const user = userCredential.user;
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || "",
        accountCredit: 25, // TESTING ONLY: Default $25 credit for testing - REMOVE IN PRODUCTION
        accountBalance: 0,
        createdAt: new Date(),
        lastUpdated: new Date()
      });
      
      // Redux will be updated by the onAuthStateChanged hook
      return userCredential;
    } catch (error: any) {
      const userFriendlyMessage = error.code ? getErrorMessage(error.code) : error.message;
      dispatch(setReduxError(userFriendlyMessage));
      setAuthError(userFriendlyMessage);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      dispatch(setLoading(true));
      console.log('Signing in with email:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Redux will be updated by the onAuthStateChanged hook
      console.log('Signed in successfully:', userCredential.user);
      return userCredential;
    } catch (error: any) {
      console.error('Sign in error:', error);
      const userFriendlyMessage = error.code ? getErrorMessage(error.code) : error.message;
      dispatch(setReduxError(userFriendlyMessage));
      setAuthError(userFriendlyMessage);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const signOutUser = async () => {
    try {
      dispatch(setLoading(true));
      await firebaseSignOut(auth);
      dispatch(clearUser());
    } catch (error: any) {
      const userFriendlyMessage = error.code ? getErrorMessage(error.code) : error.message;
      dispatch(setReduxError(userFriendlyMessage));
      setAuthError(userFriendlyMessage);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(setLoading(true));
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Map Firebase user to our UserData format
        dispatch(setUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        }));
      } else {
        dispatch(clearUser());
      }
      setCurrentUser(user);
      setAuthLoading(false);
      dispatch(setLoading(false));
    });

    return unsubscribe;
  }, [dispatch]);

  const value = {
    currentUser,
    signUp,
    signIn,
    signOut: signOutUser,
    loading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};